import { API_BASE_URL, getAuthToken, handleApiError } from './apiUtils';

export interface City {
    id: number;
    name: string;
    code: string;
    zone: string;
    status: number;
    created_at: string;
    updated_at: string;
}

export interface CreateCityData {
    name: string;
    code: string;
    zone: string;
}

export interface UpdateCityData {
    name?: string;
    code?: string;
    zone?: string;
}

export interface CitiesListParams {
    status?: string;
    query?: string;
    zone?: string;
    page?: number;
    per_page?: number;
}

export interface CitiesListResponse {
    cities: City[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export interface BackendResponse<T> {
    data: T;
    message?: string;
}

export const cityApi = {
    // Get cities list with filtering and pagination
    getCities: async (params: CitiesListParams = {}): Promise<CitiesListResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${API_BASE_URL}/cities?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<CitiesListResponse> = await response.json();
        return data.data;
    },

    // Get single city details
    getCity: async (id: number): Promise<City> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/cities/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ city: City }> = await response.json();
        return data.data.city;
    },

    // Create new city
    createCity: async (cityData: CreateCityData): Promise<City> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/cities`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(cityData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ city: City }> = await response.json();
        return data.data.city;
    },

    // Update city
    updateCity: async (id: number, cityData: UpdateCityData): Promise<City> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/cities/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(cityData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ city: City }> = await response.json();
        return data.data.city;
    },

    deleteCity: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const response = await fetch(`${API_BASE_URL}/cities/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Toggle city status (activate/deactivate)
    toggleCityStatus: async (id: number): Promise<{ id: number; name: string; status: number }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/cities/${id}/toggle-status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ city: { id: number; name: string; status: number } }> = await response.json();
        return data.data.city;
    },

    // Get zones list
    getZones: async (): Promise<string[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/cities/zones`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<string[]> = await response.json();
        return data.data;
    },
};
